import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, TextField, FormControl, Select, MenuItem, Button, InputAdornment,
    Box, Typography, Paper,
    Tooltip,
    Stack,
    Grid,
    Chip
} from '@mui/material';
import { AccountBalance, AddShoppingCart, Delete, Save, Cancel, Discount, Edit, Visibility, Warning, Search } from '@mui/icons-material';
import API from '../../services/api';
import CustomSnackBar from '../../components/CustomSnackBar';
import ChipFormat from '../../components/ChipFormat';
import GeneralModal from '../../components/GeneralModal';
import PickProduct from './PickProduct';
import PickPromotion from './PickPromotion';
import AutoCompleteBank from '../../components/AutoCompleteBank';

const getSafeNumber = (value) => {
    const num = Number(value);
    return isNaN(num) ? 0 : parseFloat(num.toFixed(2));
};

const resolveFormatId = (row) => (
    row?.prformat_id ||
    row?.format_id ||
    row?.prformat?.id ||
    row?.product?.prformat_id ||
    row?.product?.prformat?.id ||
    null
);

const BANK_REQUISITION_FLOW = 'BANK_REQUISITION';

const bankRequisitionStatusLabels = {
    PENDING_ABASTO: 'Pendiente de cargar',
    ABASTO_LOADED: 'Cargada',
    PENDING_UPLOAD: 'Pendiente de cargar',
    UPLOADED: 'Cargada',
    PARTIALLY_INVOICED: 'Parcialmente facturada',
    INVOICED: 'Facturada',
    EXPIRED_MONTHLY_CLOSE: 'Vencida por cierre mensual',
    CANCELLED: 'Cancelada',
};

const bankRequisitionStatusColors = {
    PENDING_ABASTO: 'warning',
    ABASTO_LOADED: 'info',
    PENDING_UPLOAD: 'warning',
    UPLOADED: 'info',
    PARTIALLY_INVOICED: 'info',
    INVOICED: 'success',
    EXPIRED_MONTHLY_CLOSE: 'error',
    CANCELLED: 'default',
};

const formatGTQ = (value) => new Intl.NumberFormat('es-GT', {
    style: 'currency',
    currency: 'GTQ',
    minimumFractionDigits: 2,
}).format(Number(value || 0));

const formatDateLabel = (value) => {
    if (!value) return '-';
    const text = String(value);
    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
        return `${text.slice(8, 10)}/${text.slice(5, 7)}/${text.slice(0, 4)}`;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('es-GT');
};

export default function EditOrder({ order, setOrder, setOpen, updateOrder, onEditAbasto }) {
    const safeOrder = useMemo(() => (
        order || { order_details: [], order_bonus: [], supplier: null }
    ), [order]);
    const supplierLabel = (
        safeOrder?.supplier?.companyName
        || safeOrder?.supplier?.legalName
        || safeOrder?.supplier?.name
        || safeOrder?.supplier_data?.companyName
        || safeOrder?.supplier_data?.legalName
        || (typeof safeOrder?.supplier === 'string' ? safeOrder.supplier : '')
        || safeOrder?.supplier_name
        || (safeOrder?.supplier_id ? `Proveedor ${safeOrder.supplier_id}` : '-')
    );
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });
    const [openPromotionModal, setOpenPromotionModal] = useState(false);
    const [selectedPromotions, setSelectedPromotions] = useState([]);
    const [totals, setTotals] = useState({
        total: 0,
        subTotal: 0,
        bonusDiscount: 0,
        priceDiscount: 0
    });
    const [warehouses, setWarehouses] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [availableProducts, setAvailableProducts] = useState([]);
    const [productsLoading, setProductsLoading] = useState(false);
    const [openProductModal, setOpenProductModal] = useState(false);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const supplierId = safeOrder?.supplier_id || safeOrder?.supplier?.id || safeOrder?.supplier_data?.id || null;
    const supplierPaymentFlow = (
        safeOrder?.supplier_data?.payment_flow_type
        || (typeof safeOrder?.supplier === 'object' ? safeOrder?.supplier?.payment_flow_type : null)
        || safeOrder?.supplier_payment_flow_type
        || 'NORMAL'
    );
    const isAbastoProvider = String(supplierPaymentFlow).toUpperCase() === BANK_REQUISITION_FLOW;
    const abastoRequisition = safeOrder?.bank_requisition || null;
    const abastoDocumentUrl = abastoRequisition?.document_url || abastoRequisition?.document || '';
    const abastoInvoices = abastoRequisition?.invoices || [];
    const abastoObligation = abastoRequisition?.obligation_summary || null;

    const orderProductIdSet = useMemo(() => (
        new Set(products.map((product) => Number(product?.product?.id || product?.product_id || product?.id)).filter(Boolean))
    ), [products]);


    useEffect(() => {
        if (safeOrder.order_details && products.length === 0) {
            const initialProducts = safeOrder.order_details.map(item => ({
                ...item,
                originalListPrice: getSafeNumber(item.listPrice || 0),
                originalFinalPrice: getSafeNumber(item.finalPrice || 0),
                listPrice: getSafeNumber(item.listPrice),
                bonusPrice: getSafeNumber(item.bonusPrice),
                finalPrice: getSafeNumber(item.finalPrice),
                // normalize format object for display
                prformat: item.prformat || item.product?.prformat || null,
                prformat_id: resolveFormatId(item),
                format_id: resolveFormatId(item),
            }));

            setProducts(initialProducts);
            updateTotals(initialProducts);

        }
    }, [products.length, safeOrder.order_details]);

    // preload warehouses
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const { data } = await API.get('catalogs/warehouses/');
                if (mounted) setWarehouses(Array.isArray(data) ? data : []);
            } catch { if (mounted) setWarehouses([]); }
        })();
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        let mounted = true;

        if (!supplierId) {
            setAvailableProducts([]);
            return undefined;
        }

        setProductsLoading(true);
        API.post('product/getProductsBySupplier/', { supplier_id: supplierId })
            .then(({ data }) => {
                if (!mounted) return;
                setAvailableProducts(Array.isArray(data) ? data : data?.results || []);
            })
            .catch((error) => {
                console.error('Error cargando productos del proveedor', error);
                if (mounted) {
                    setAvailableProducts([]);
                }
            })
            .finally(() => {
                if (mounted) {
                    setProductsLoading(false);
                }
            });

        return () => {
            mounted = false;
        };
    }, [supplierId]);

    // set warehouse_id/storage from nested order.warehouse when order changes
    useEffect(() => {
        if (!safeOrder) return;
        const wid = safeOrder.warehouse_id || safeOrder.warehouse?.id || null;
        const wname = safeOrder.storage || safeOrder.warehouse?.name || '';
        if (wid !== safeOrder.warehouse_id || wname !== safeOrder.storage) {
            setOrder(prev => ({ ...(prev || {}), warehouse_id: wid, storage: wname }));
        }
    }, [safeOrder, safeOrder?.warehouse_id, safeOrder?.warehouse?.id, safeOrder?.storage, setOrder]);

    const updateTotals = (productsList) => {
        const calculatedTotals = productsList.reduce((acc, product) => {
            const listPrice = getSafeNumber(product.listPrice || 0);
            const bonusPrice = getSafeNumber(product.bonusPrice || 0);
            const finalPrice = getSafeNumber(product.finalPrice || 0);
            const quantity = Math.max(1, getSafeNumber(product.quantity));

            // Cálculos
            const productTotal = listPrice * quantity;
            const productSubTotal = finalPrice * quantity;
            const productBonusDiscount = bonusPrice > 0 ? (listPrice - bonusPrice) * quantity : 0;
            const productPriceDiscount = (listPrice - finalPrice) * quantity - productBonusDiscount;

            return {
                total: acc.total + productTotal,
                subTotal: acc.subTotal + productSubTotal,
                bonusDiscount: acc.bonusDiscount + productBonusDiscount,
                priceDiscount: acc.priceDiscount + Math.max(0, productPriceDiscount)
            };
        }, { total: 0, subTotal: 0, bonusDiscount: 0, priceDiscount: 0 });

        const updatedTotals = {
            total: calculatedTotals.total.toFixed(2),
            subTotal: calculatedTotals.subTotal.toFixed(2),
            bonusDiscount: calculatedTotals.bonusDiscount.toFixed(2),
            priceDiscount: calculatedTotals.priceDiscount.toFixed(2)
        };

        setTotals(updatedTotals);

    };

    const handleValueChange = (index, field, value) => {
        setProducts(prevProducts => {
            const updatedProducts = [...prevProducts];

            const isNumericField = ['quantity', 'bonusPrice', 'listPrice', 'finalPrice'].includes(field);
            updatedProducts[index][field] = value === '' ? null : (isNumericField ? getSafeNumber(value) : value);

            updateTotals(updatedProducts);
            return updatedProducts;
        });

        console.log("Cambio en:", field, "valor:", value);
    };


    const handleBonusChange = (bonusKey, field, value) => {
        setOrder(prevOrder => ({
            ...(prevOrder || { order_bonus: [] }),
            order_bonus: (prevOrder?.order_bonus || []).map(bonus => {
                const key = bonus.id || bonus.tempId;
                return key === bonusKey
                    ? { ...bonus, [field]: value }
                    : bonus;
            })
        }));
    };

    const handleDetailFormatChange = (index, fmt) => {
        setProducts(prev => prev.map((row, i) => (
            i === index
                ? { ...row, prformat_id: fmt?.id || null, prformat: fmt || null }
                : row
        )));
    };

    const handleBonusFormatChange = (bonusKey, fmt) => {
        setOrder(prev => ({
            ...(prev || { order_bonus: [] }),
            order_bonus: (prev?.order_bonus || []).map(b => {
                const key = b.id || b.tempId;
                return key === bonusKey ? { ...b, prformat_id: fmt?.id || null, prformat: fmt || null } : b;
            })
        }));
    };

    const handleWarehouseSelect = (name, selected) => {
        if (!selected) return;
        setOrder(prev => ({
            ...(prev || {}),
            warehouse_id: selected.id,
            storage: selected.name,
        }));
    };

    useEffect(() => {
        console.log('order actualizado:', order);
    }, [order]);

    const handleAddPromotion = () => {
        setOpenPromotionModal(true);
    };
    const handleAddPromotionsToOrder = () => {
        if (selectedPromotions.length === 0) return;

        // Crear bonificaciones según el nuevo modelo
        const newBonuses = selectedPromotions.map(product => ({
            tempId: `new-${Date.now()}`, // Agregar tempId único
            product_id: product.id,
            product: product, // Mantener referencia al producto completo
            description: product.description,
            code: product.code,
            quantity: 1,
            prformat: product.prformat,
            prformat_id: product.prformat_id || product.prformat?.id || null,
            reason: null,
            is_global: false, // Agregar is_global
        }));

        // Actualizar el estado de la orden
        setOrder(prevOrder => ({
            ...(prevOrder || { order_bonus: [] }),
            order_bonus: [...((prevOrder && prevOrder.order_bonus) || []), ...newBonuses]
        }));

        setSelectedPromotions([]);
        setOpenPromotionModal(false);
    };

    const handleAddProductsToOrder = useCallback(() => {
        if (selectedProducts.length === 0) return;

        const freshProducts = selectedProducts.filter((product) => !orderProductIdSet.has(Number(product?.id)));

        if (freshProducts.length === 0) {
            setSnack({
                open: true,
                message: 'Los productos seleccionados ya fueron agregados',
                severity: 'warning'
            });
            return;
        }

        if (freshProducts.length < selectedProducts.length) {
            setSnack({
                open: true,
                message: 'Se omitieron productos duplicados',
                severity: 'warning'
            });
        }

        const timestamp = Date.now();
        const newDetails = freshProducts.map((product, index) => {
            const activePrice = (product.prices || []).find((price) => price?.is_active) || product.prices?.[0] || null;
            const basePrice = getSafeNumber(activePrice?.price || 0);

            return {
                tempId: `new-${timestamp}-${product.id}-${index}`,
                product_id: product.id,
                product,
                code: product.code,
                description: product.description,
                quantity: 1,
                prformat: product.prformat || null,
                prformat_id: resolveFormatId(product),
                format_id: resolveFormatId(product),
                listPrice: basePrice,
                bonusPrice: 0,
                finalPrice: basePrice,
                originalListPrice: basePrice,
                originalFinalPrice: basePrice,
            };
        });

        setProducts((prevProducts) => {
            const nextProducts = [...prevProducts, ...newDetails];
            updateTotals(nextProducts);
            return nextProducts;
        });
        setSelectedProducts([]);
        setOpenProductModal(false);
    }, [orderProductIdSet, selectedProducts]);

    const handleDeleteBonus = (bonusKey) => {
        setOrder(prevOrder => ({
            ...(prevOrder || { order_bonus: [] }),
            order_bonus: (prevOrder?.order_bonus || []).filter(b => {
                const key = b.id || b.tempId;
                return key !== bonusKey;
            })
        }));
    };


    const handleSave = async () => {
        try {
            setLoading(true);

            const payload = {
                details: products.map(p => ({
                    id: p.id,
                    product_id: p.product_id || p.product?.id || null,
                    quantity: p.quantity,
                    listPrice: p.listPrice,
                    bonusPrice: p.bonusPrice,
                    finalPrice: p.finalPrice,
                    prformat_id: resolveFormatId(p)
                })),
                bonuses: (safeOrder.order_bonus || []).map(b => ({
                    id: b.tempId ? undefined : b.id,
                    product_id: b.product.id,
                    quantity: b.quantity,
                    reason: b.reason,
                    prformat_id: resolveFormatId(b),
                    is_global: b.is_global || false
                })),
                total: totals.total,
                subTotal: totals.subTotal,
                bonusDiscount: totals.bonusDiscount,
                priceDiscount: totals.priceDiscount,
                warehouse_id: safeOrder.warehouse_id || safeOrder.warehouse?.id || null
            };

            const response = await API.patch(`order/${safeOrder.id}/update_order/`, payload);

            setOrder(response.data || safeOrder);

            setSnack({
                open: true,
                message: `Orden guardada`,
                severity: 'success'
            });

            setTimeout(() => {
                updateOrder();
                setOpen(false);
            }, 1500);
        } catch (error) {
            console.error("Error al guardar:", error);
            setSnack({
                open: true,
                message: error.response?.data?.message || 'Error al guardar',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const getProductRowKey = useCallback((product) => (
        product?.id ?? product?.product?.id ?? product?.code
    ), []);

    const filteredProducts = useMemo(() => {
        const term = String(searchTerm || '').trim().toLowerCase();
        if (!term) return products;
        return (products || []).filter((product) => {
            const code = String(product?.product?.code ?? product?.code ?? '').toLowerCase();
            const description = String(product?.product?.description ?? product?.description ?? '').toLowerCase();
            return code.includes(term) || description.includes(term);
        });
    }, [products, searchTerm]);


    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {products.some(p =>
                (p.listPrice || 0) > (p.originalListPrice || 0) ||  // Solo >, no >=
                (p.finalPrice || 0) > (p.originalFinalPrice || 0)
            ) && (
                    <Typography color="warning.main" variant="body2" sx={{
                        textAlign: 'right',
                        mt: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}>
                        <Warning fontSize="small" />
                        ¡Advertencia: Uno o más precios han aumentado respecto al valor original!
                    </Typography>
                )}
            {/* Header info: supplier and warehouse selector */}
            {/* Header bonito */}
            <Paper
                variant="outlined"
                sx={{
                    p: 1.25,
                    borderRadius: 2,
                    mb: 1,
                    bgcolor: "background.paper",
                }}
            >
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    justifyContent="space-between"
                >
                    {/* Proveedor */}
                    <Box sx={{ minWidth: 220 }}>
                        <Typography variant="caption" color="text.secondary">
                            Proveedor
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                            {supplierLabel}
                        </Typography>
                    </Box>

                    {/* Bodega destino (más pro y compacta) */}
                    <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 340 } }}>
                        <AutoCompleteBank
                            options={warehouses}
                            getOptionLabel={(w) => w.name}
                            label="Bodega destino"
                            name="storage"
                            disabled={loading || warehouses.length === 0}
                            value={(() => {
                                const wid = safeOrder?.warehouse_id;
                                if (wid) return warehouses.find(w => String(w.id) === String(wid)) || null;
                                if (safeOrder?.storage) return warehouses.find(w => w.name === safeOrder.storage) || null;
                                return null;
                            })()}
                            onChange={handleWarehouseSelect}
                            // Si tu AutoCompleteBank acepta props para el TextField interno (muchos lo aceptan)
                            textFieldProps={{
                                size: "small",
                                placeholder: warehouses.length ? "Selecciona una bodega" : "Sin bodegas",
                            }}
                            sx={{
                                "& .MuiInputBase-root": { borderRadius: 2 },
                            }}
                        />

                        {warehouses.length === 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                                Sin bodegas disponibles para asignar.
                            </Typography>
                        )}
                    </Box>
                </Stack>
            </Paper>

            {isAbastoProvider && (
                <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1, bgcolor: 'rgba(16, 23, 60, 0.03)' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                        <Stack direction="row" spacing={1} alignItems="center">
                            <AccountBalance sx={{ color: '#10173c' }} />
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                    Abasto
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {abastoRequisition?.id ? 'Datos de Abasto cargados' : 'Abasto pendiente de cargar'}
                                </Typography>
                            </Box>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                            {abastoRequisition?.status && (
                                <Chip
                                    size="small"
                                    label={bankRequisitionStatusLabels[abastoRequisition.status] || abastoRequisition.status}
                                    color={bankRequisitionStatusColors[abastoRequisition.status] || 'default'}
                                    variant="outlined"
                                />
                            )}
                            {abastoDocumentUrl && (
                                <Tooltip title="Ver documento">
                                    <IconButton size="small" color="primary" onClick={() => window.open(abastoDocumentUrl, '_blank', 'noopener,noreferrer')}>
                                        <Visibility fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {onEditAbasto && (
                                <Tooltip title={abastoRequisition?.id ? 'Editar Abasto' : 'Cargar Abasto'}>
                                    <IconButton size="small" color="info" onClick={onEditAbasto}>
                                        <Edit fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Stack>
                    </Stack>

                    {abastoRequisition?.id && (
                        <Grid container spacing={1.25} sx={{ mt: 1 }}>
                            <Grid item xs={12} sm={3}>
                                <Typography variant="caption" color="text.secondary">No. requisicion</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{abastoRequisition.requisition_number || '-'}</Typography>
                            </Grid>
                            <Grid item xs={6} sm={2}>
                                <Typography variant="caption" color="text.secondary">Fecha</Typography>
                                <Typography variant="body2">{formatDateLabel(abastoRequisition.requisition_date)}</Typography>
                            </Grid>
                            <Grid item xs={6} sm={2}>
                                <Typography variant="caption" color="text.secondary">Monto</Typography>
                                <Typography variant="body2">{formatGTQ(abastoRequisition.requested_amount)}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <Typography variant="caption" color="text.secondary">Documento</Typography>
                                <Typography variant="body2">{abastoDocumentUrl ? 'Cargado' : 'Pendiente'}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <Typography variant="caption" color="text.secondary">Facturas conciliadas</Typography>
                                <Typography variant="body2">
                                    {abastoInvoices.length
                                        ? abastoInvoices.map((invoice) => invoice.label || invoice.number || invoice.id).join(', ')
                                        : 'Sin facturas'}
                                </Typography>
                            </Grid>
                            {abastoObligation && (
                                <Grid item xs={12}>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                                        <Chip size="small" label={`Despacho ${abastoObligation.dispatch_number || abastoObligation.id}`} color="info" variant="outlined" />
                                        <Typography variant="body2" color="text.secondary">
                                            Saldo bancario {formatGTQ(abastoObligation.balance)}
                                        </Typography>
                                    </Stack>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </Paper>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant="outlined"
                    startIcon={<AddShoppingCart />}
                    onClick={() => setOpenProductModal(true)}
                    disabled={productsLoading || !supplierId}
                    sx={{ textTransform: 'none' }}
                >
                    Agregar productos
                </Button>
            </Box>

            {/* Search bonito estilo “standard” */}
            <TextField
                label="Buscar"
                variant="standard"
                fullWidth
                value={searchTerm}
                size="small"
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Search fontSize="small" />
                        </InputAdornment>
                    ),
                }}
                sx={{ mb: 1 }}
            />


            <TableContainer component={Paper}>
                <Table sx={{ width: '100%' }} size="small">
                    <TableHead sx={{ background: "#10173c" }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: "bold", color: "white" }}>Producto</TableCell>
                            <TableCell sx={{ fontWeight: "bold", color: "white" }} align="center">Cantidad</TableCell>
                            <TableCell sx={{ fontWeight: "bold", color: "white" }} align="right">Precio Lista</TableCell>
                            <TableCell sx={{ fontWeight: "bold", color: "white" }} align="right">Precio Bonif.</TableCell>
                            <TableCell sx={{ fontWeight: "bold", color: "white" }} align="right">Precio Final</TableCell>
                            <TableCell sx={{ fontWeight: "bold", color: "white" }} align="right">Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredProducts.map((product, idx) => {
                            const sourceIndex = products.indexOf(product);
                            if (sourceIndex < 0) return null;
                            const rowKey = `${getProductRowKey(product) ?? 'row'}-${sourceIndex}`;
                            return (
                                <React.Fragment key={rowKey}>
                                    <TableRow>
                                        <TableCell>
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                flexWrap: 'wrap'
                                            }}>
                                                {/* Formato (si existe) */}
                                                {product && (<ChipFormat value={product.prformat || product.prformat_id || ''} itemKey={rowKey} onFormatChange={(itemKey, value) => handleDetailFormatChange(sourceIndex, value)} />)}

                                                {/* Nombre del producto y unidades */}
                                                <Stack
                                                    spacing={1}
                                                    direction="row"
                                                    alignItems="center"
                                                    sx={{
                                                        ml: product.prformat ? 0 : 1 // Ajuste de margen izquierdo
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: 500,
                                                            textTransform: 'capitalize',
                                                            fontSize: '0.9rem'
                                                        }}
                                                    >
                                                        {String(product.product?.description || '')}
                                                    </Typography>

                                                    {/* Unidades */}
                                                    {product.product?.units !== undefined && product.product?.units !== null && (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: 'text.secondary',
                                                                fontSize: '0.75rem'
                                                            }}
                                                        >
                                                            ({typeof product.product.units === 'number'
                                                                ? product.product.units
                                                                : String(product.product.units).toLowerCase()})
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </Box>
                                        </TableCell>

                                        <TableCell align="center">
                                            <TextField
                                                type="number"
                                                value={product.quantity}
                                                onChange={(e) => handleValueChange(sourceIndex, 'quantity', e.target.value)}
                                                size="small"
                                                inputProps={{ min: 1 }}
                                                sx={{ width: "100px" }}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <FormControl size="small" sx={{ width: '120px' }}>
                                                <Select
                                                    value={product.listPrice || ''}
                                                    onChange={(e) => handleValueChange(sourceIndex, 'listPrice', e.target.value)}
                                                    displayEmpty
                                                    inputProps={{ 'aria-label': 'Precio lista' }}
                                                    sx={{
                                                        '& .MuiSelect-select': {
                                                            color: (product.listPrice || 0) > (product.originalListPrice || 0) // Solo >, no >=
                                                                ? 'warning.main'
                                                                : 'inherit'
                                                        }
                                                    }}
                                                    renderValue={(selected) => {
                                                        if (!selected) return <em>Seleccionar</em>;
                                                        return selected;
                                                    }}
                                                >
                                                    <MenuItem disabled value="">
                                                        <em>Seleccione precio</em>
                                                    </MenuItem>
                                                    {product.product?.prices?.map((priceItem) => (
                                                        <MenuItem key={priceItem.id} value={priceItem.price}>
                                                            {priceItem.price}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                        <TableCell align="right">
                                            <TextField
                                                type="number"
                                                value={product.bonusPrice}
                                                onChange={(e) => handleValueChange(sourceIndex, 'bonusPrice', e.target.value)}
                                                size="small"
                                                inputProps={{ min: 0, step: "0.01" }}
                                                sx={{ width: "100px" }}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <TextField
                                                type="number"
                                                value={product.finalPrice}
                                                onChange={(e) => handleValueChange(sourceIndex, 'finalPrice', e.target.value)}
                                                size="small"
                                                inputProps={{ min: 0, step: "0.01" }}
                                                sx={{
                                                    width: "100px",
                                                    '& .MuiInput-input': {
                                                        color: (product.finalPrice || 0) > (product.originalFinalPrice || 0)
                                                            ? 'warning.main'
                                                            : 'inherit'
                                                    }
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Stack spacing={1} direction={{ sx: 'column', sm: 'row' }}>
                                                <Tooltip title="Eliminar">
                                                    <IconButton onClick={() => {
                                                        const newProducts = products.filter((_, i) => i !== sourceIndex);
                                                        setProducts(newProducts);
                                                        updateTotals(newProducts);
                                                    }}>
                                                        <Delete color="error" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Agregar bonificación">
                                                    <IconButton onClick={() => handleAddPromotion(sourceIndex)}>
                                                        <Discount color="success" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>

                                </React.Fragment>
                            )
                        })}
                    </TableBody>


                    {(safeOrder.order_bonus || []).length > 0 && (
                        <>
                            {safeOrder.order_bonus.map((bonus, bIdx) => (
                                <TableRow
                                    key={`bonus-${bIdx}`}
                                    sx={{
                                        '& td': {
                                            py: '4px',
                                            color: 'success.main',
                                            fontSize: '0.8rem',
                                            borderBottom: '1px dashed rgba(0, 0, 0, 0.12)',
                                        },
                                    }}
                                >
                                    <TableCell>
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            flexWrap: 'wrap'
                                        }}>
                                            {/* Formato (si existe) */}
                                            {bonus && (<ChipFormat value={bonus.prformat || bonus.prformat_id || ''} itemKey={bonus.id || bonus.tempId} chipColor='success' onFormatChange={handleBonusFormatChange} />)}

                                            {/* Nombre del producto y unidades */}
                                            <Stack
                                                spacing={1}
                                                direction="row"
                                                alignItems="center"
                                                sx={{
                                                    ml: bonus.prformat ? 0 : 1 // Ajuste de margen izquierdo
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: 500,
                                                        textTransform: 'capitalize',
                                                        fontSize: '0.8rem'
                                                    }}
                                                >
                                                    {String(bonus.product?.description || bonus.product?.description) + ' (Bonif.)'}
                                                </Typography>

                                                {/* Unidades */}
                                                {bonus.product?.units !== undefined && bonus.product?.units !== null && (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: 'text.secondary',
                                                            fontSize: '0.75rem'
                                                        }}
                                                    >
                                                        ({typeof bonus.product.units === 'number'
                                                            ? bonus.product.units
                                                            : String(bonus.product.units).toLowerCase()})
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </Box>



                                    </TableCell>
                                    <TableCell align="center">
                                        <TextField
                                            type="number"
                                            value={bonus.quantity}
                                            onChange={(e) => handleBonusChange(bonus.id || bonus.tempId, 'quantity', e.target.value)}
                                            size="small"
                                            inputProps={{ min: 1 }}
                                            sx={{ width: "100px" }}
                                        />
                                    </TableCell>
                                    <TableCell colSpan={3}>
                                        <TextField
                                            value={bonus.reason}
                                            onChange={(e) => handleBonusChange(bonus.id || bonus.tempId, 'reason', e.target.value)}
                                            size="small"
                                            fullWidth
                                            placeholder="Razón"
                                        />
                                    </TableCell>
                                    <TableCell align="right" colSpan={2}>
                                        <Tooltip title="Eliminar bonificación">
                                            <IconButton onClick={() => handleDeleteBonus(bonus.id || bonus.tempId, bIdx)}>
                                                <Delete color="error" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </>
                    )}
                </Table>
            </TableContainer>
            <Paper elevation={3} sx={{ p: 1, bgcolor: 'background.paper' }}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))',
                        rowGap: 1,
                        columnGap: 3,
                    }}
                >
                    {Object.entries(totals).map(([key, value]) => (
                        <Box key={key}>
                            <Typography variant="body2" color="textSecondary">
                                {key === 'bonusDiscount'
                                    ? 'Desc. Bonif.'
                                    : key === 'priceDiscount'
                                        ? 'Desc. Precio'
                                        : key === 'subTotal'
                                            ? 'Subtotal'
                                            : 'Total'}
                            </Typography>
                            <Typography variant="subtitle1" fontWeight="bold">
                                {new Intl.NumberFormat('es-GT', {
                                    style: 'currency',
                                    currency: 'GTQ',
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                }).format(value)}
                            </Typography>

                        </Box>
                    ))}
                </Box>
            </Paper>


            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                    variant="outlined"
                    startIcon={<Cancel />}
                    onClick={() => setOpen(false)}
                    disabled={loading}
                >
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={handleSave}
                    disabled={loading || products.length === 0}
                >
                    {loading ? 'Guardando...' : 'Guardar'}
                </Button>
            </Box>

            {/* Notificación */}
            <CustomSnackBar
                open={snack.open}
                setOpen={(val) => setSnack(prev => ({ ...prev, open: val }))}
                message={snack.message}
                severity={snack.severity}
            />
            <GeneralModal
                open={openProductModal}
                setOpen={setOpenProductModal}
                handleClose={() => setOpenProductModal(false)}
                component={PickProduct}
                width='85%'
                componentProps={{
                    products: availableProducts,
                    selectedProducts,
                    setSelectedProducts,
                    handleAddToCart: handleAddProductsToOrder,
                    excludedProductIds: orderProductIdSet,
                    supplierId,
                }}
            />
            <GeneralModal
                open={openPromotionModal}
                setOpen={setOpenPromotionModal}
                handleClose={() => setOpenPromotionModal(false)}
                component={PickPromotion}
                width='80%'
                // noPadding={false}
                componentProps={{
                    products,
                    selectedPromotions,
                    setSelectedPromotions,
                    handleAddPromotions: handleAddPromotionsToOrder
                }}
            />
        </Box>
    );
}

